import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Button from '../../../components/ui/Button';
import CustomerModal from './components/CustomerModal';
import SupplierModal from './components/SupplierModal';
import ClientModal from '../../client-management/components/ClientModal';
import { API_ENDPOINTS } from '../../../utils/config';
import { 
  Users, 
  Building2,
  Package,
  Plus, 
  Search, 
  Filter,
  Edit,
  Trash2,
  Eye,
  Phone,
  Mail,
  DollarSign,
  FileText,
  TrendingUp,
  Package2,
  AlertCircle,
  CheckCircle,
  Info,
  AlertTriangle
} from 'lucide-react';
import CustomerDetailsModal from './components/CustomerDetailsModal';
import SupplierDetailsModal from './components/SupplierDetailsModal';

const Contacts = () => {
  const [activeTab, setActiveTab] = useState('customers');
  const [searchTerm, setSearchTerm] = useState('');
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [modalMode, setModalMode] = useState('add');
  const [notification, setNotification] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [detailsError, setDetailsError] = useState('');
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);
  const [viewingCustomerId, setViewingCustomerId] = useState(null);
  const [deletingCustomerId, setDeletingCustomerId] = useState(null);
  const [prefetchingCustomerId, setPrefetchingCustomerId] = useState(null);
  const [isHydratingCustomers, setIsHydratingCustomers] = useState(false);
  const [customerPendingDelete, setCustomerPendingDelete] = useState(null);
  
  // Supplier state
  const [suppliers, setSuppliers] = useState([]);
  const [isLoadingSuppliers, setIsLoadingSuppliers] = useState(false);
  const [isSupplierModalOpen, setIsSupplierModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierModalMode, setSupplierModalMode] = useState('add');
  const [isSupplierDetailsModalOpen, setIsSupplierDetailsModalOpen] = useState(false);
  const [supplierDetails, setSupplierDetails] = useState(null);
  const [supplierDetailsError, setSupplierDetailsError] = useState('');
  const [isSupplierDetailsLoading, setIsSupplierDetailsLoading] = useState(false);
  const [viewingSupplierId, setViewingSupplierId] = useState(null);
  const [deletingSupplierId, setDeletingSupplierId] = useState(null);
  const [prefetchingSupplierId, setPrefetchingSupplierId] = useState(null);
  const [isHydratingSuppliers, setIsHydratingSuppliers] = useState(false);
  const [supplierPendingDelete, setSupplierPendingDelete] = useState(null);

  // Consolidated invoices state
  const [isConsolidatedOpen, setIsConsolidatedOpen] = useState(false);
  const [consolidatedCustomer, setConsolidatedCustomer] = useState(null);
  const [consolidatedInvoices, setConsolidatedInvoices] = useState([]);
  const [isConsolidatedLoading, setIsConsolidatedLoading] = useState(false);
  const [consolidatedError, setConsolidatedError] = useState('');

  const consolidatedTotals = useMemo(() => {
    const totals = {
      totalInvoiced: 0,
      totalPaid: 0,
      totalPartial: 0,
      totalPending: 0,
    };
    consolidatedInvoices.forEach((inv) => {
      const total = parseFloat(inv.total_amount || 0) || 0;
      const fullyPaid = parseFloat(inv.payment_status?.fully_paid || 0) || 0;
      const partialPaid = parseFloat(inv.payment_status?.partial_paid || 0) || 0;
      const receivable = parseFloat(inv.payment_status?.receivable || (total - fullyPaid - partialPaid)) || 0;
      totals.totalInvoiced += total;
      totals.totalPaid += fullyPaid;
      totals.totalPartial += partialPaid;
      totals.totalPending += receivable;
    });
    return totals;
  }, [consolidatedInvoices]);

  const formatCurrency = (value) =>
    `₹${(parseFloat(value || 0) || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  // Clients state
  const [clients, setClients] = useState([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [clientModalMode, setClientModalMode] = useState('add');
  const [selectedClient, setSelectedClient] = useState(null);
  const [deletingClientId, setDeletingClientId] = useState(null);

  const getAuthHeaders = (includeJson = false) => {
    const token = localStorage.getItem('token');
    return {
      Accept: 'application/json',
      ...(includeJson ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
  };

  const resolveUrl = (url, baseUrl) => {
    if (!url) return null;
    try {
      const base = baseUrl || API_ENDPOINTS.CUSTOMERS_LIST;
      return new URL(url, base).toString();
    } catch (error) {
      return null;
    }
  };

  const extractCustomersFromPayload = (payload) => {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload.results)) return payload.results;
    if (Array.isArray(payload.data?.results)) return payload.data.results;
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.customers)) return payload.customers;
    if (Array.isArray(payload.items)) return payload.items;
    return [];
  };

  const needsDetailHydration = (customersList) => {
    if (!Array.isArray(customersList)) return false;
    return customersList.some((customer) => {
      const data = customer?.customer_data;
      if (!data) return true;
      return !data?.basic_information;
    });
  };

  const hydrateCustomersWithDetails = async (customersList, headers) => {
    if (!Array.isArray(customersList) || customersList.length === 0) return customersList;
    const hydrated = [];
    for (const customer of customersList) {
      const missingDetail = !customer?.customer_data?.basic_information;
      if (!customer?.id || !missingDetail) {
        hydrated.push(customer);
        continue;
      }
      try {
        const detailResponse = await axios.get(API_ENDPOINTS.CUSTOMERS_DETAIL(customer.id), { headers });
        const detailData = detailResponse.data || {};
        hydrated.push({
          ...customer,
          ...detailData,
          customer_data: detailData.customer_data || customer.customer_data
        });
      } catch (error) {
        hydrated.push(customer);
      }
    }
    return hydrated;
  };

  const loadCustomerDetails = async (customerId) => {
    const response = await axios.get(
      API_ENDPOINTS.CUSTOMERS_DETAIL(customerId),
      { headers: getAuthHeaders() }
    );
    return response.data;
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return 'N/A';
    const parsedDate = new Date(dateString);
    return Number.isNaN(parsedDate.getTime()) ? 'N/A' : parsedDate.toLocaleDateString();
  };

  // Fetch customers on component mount
  useEffect(() => {
    fetchCustomers();
  }, []);

  // Fetch suppliers when suppliers tab is active

  useEffect(() => {
    if (notification?.message) {
      const timer = setTimeout(() => setNotification(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const fetchCustomers = async () => {
    setIsLoading(true);
    try {
      const headers = getAuthHeaders();
      let url = API_ENDPOINTS.CUSTOMERS_LIST;
      let aggregatedCustomers = [];
      let safetyCounter = 0;
      const MAX_PAGES = 20;

      while (url && safetyCounter < MAX_PAGES) {
        const response = await axios.get(url, { headers });
        const pageCustomers = extractCustomersFromPayload(response.data);
        if (pageCustomers.length > 0) {
          aggregatedCustomers = aggregatedCustomers.concat(pageCustomers);
        } else if (!Array.isArray(response.data) && response.data?.customer_data) {
          aggregatedCustomers.push(response.data);
        }

        const nextUrl = response.data?.next || response.data?.links?.next || response.data?.pagination?.next;
        url = resolveUrl(nextUrl, API_ENDPOINTS.CUSTOMERS_LIST);
        if (!url) {
          break;
        }
        safetyCounter += 1;
      }

      if (needsDetailHydration(aggregatedCustomers)) {
        setIsHydratingCustomers(true);
        aggregatedCustomers = await hydrateCustomersWithDetails(aggregatedCustomers, headers);
        setIsHydratingCustomers(false);
      }

      setCustomers(aggregatedCustomers);
    } catch (error) {
      setCustomers([]);
      setIsHydratingCustomers(false);
      const apiError = error?.response?.data?.message || error?.response?.data?.detail || 'Unable to load customers.';
      setNotification({ type: 'error', message: apiError });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddCustomer = () => {
    setSelectedCustomer(null);
    setModalMode('add');
    setIsCustomerModalOpen(true);
  };

  const handleEditCustomer = async (customer) => {
    if (!customer?.id) return;
    setPrefetchingCustomerId(customer.id);
    try {
      const detailedCustomer = await loadCustomerDetails(customer.id);
      setSelectedCustomer(detailedCustomer);
      setModalMode('edit');
      setIsCustomerModalOpen(true);
    } catch (error) {
      const apiError = error?.response?.data?.message || 'Unable to load customer details for editing.';
      setNotification({ type: 'error', message: apiError });
    } finally {
      setPrefetchingCustomerId(null);
    }
  };

  const handleCustomerSave = () => {
    fetchCustomers();
    setNotification({
      type: 'success',
      message: modalMode === 'edit' ? 'Customer updated successfully.' : 'Customer created successfully.'
    });
  };

  const handleCloseModal = () => {
    setIsCustomerModalOpen(false);
    setSelectedCustomer(null);
  };

  const handleViewCustomer = async (customerId) => {
    if (!customerId) return;
    setViewingCustomerId(customerId);
    setIsDetailsModalOpen(true);
    setIsDetailsLoading(true);
    setDetailsError('');
    setCustomerDetails(null);
    try {
      const detail = await loadCustomerDetails(customerId);
      setCustomerDetails(detail);
    } catch (error) {
      const apiError = error?.response?.data?.message || error?.response?.data?.detail || 'Unable to fetch customer details.';
      setDetailsError(apiError);
    } finally {
      setIsDetailsLoading(false);
      setViewingCustomerId(null);
    }
  };

  const handleCloseDetails = () => {
    setIsDetailsModalOpen(false);
    setCustomerDetails(null);
    setDetailsError('');
  };

  const handleDeleteCustomerRequest = (customer) => {
    if (!customer?.id) return;
    setCustomerPendingDelete(customer);
  };

  const handleDeleteCustomerConfirm = async () => {
    const customerId = customerPendingDelete?.id;
    if (!customerId) return;

    setDeletingCustomerId(customerId);
    try {
      await axios.delete(API_ENDPOINTS.CUSTOMERS_DELETE(customerId), {
        headers: getAuthHeaders()
      });
      setNotification({ type: 'success', message: 'Customer deleted successfully.' });
      await fetchCustomers();
    } catch (error) {
      const apiError = error?.response?.data?.message || error?.response?.data?.detail || 'Failed to delete customer.';
      setNotification({ type: 'error', message: apiError });
    } finally {
      setDeletingCustomerId(null);
      setCustomerPendingDelete(null);
    }
  };

  // Supplier functions
  const extractSuppliersFromPayload = (payload) => {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload.results)) return payload.results;
    if (Array.isArray(payload.data?.results)) return payload.data.results;
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.suppliers)) return payload.suppliers;
    if (Array.isArray(payload.items)) return payload.items;
    return [];
  };

  const needsSupplierDetailHydration = (suppliersList) => {
    if (!Array.isArray(suppliersList)) return false;
    return suppliersList.some((supplier) => {
      const data = supplier?.supplier_data;
      if (!data) return true;
      return !data?.basic_information;
    });
  };

  const hydrateSuppliersWithDetails = async (suppliersList, headers) => {
    if (!Array.isArray(suppliersList) || suppliersList.length === 0) return suppliersList;
    const hydrated = [];
    for (const supplier of suppliersList) {
      const missingDetail = !supplier?.supplier_data?.basic_information;
      if (!supplier?.id || !missingDetail) {
        hydrated.push(supplier);
        continue;
      }
      try {
        const detailResponse = await axios.get(API_ENDPOINTS.SUPPLIERS_DETAIL(supplier.id), { headers });
        const detailData = detailResponse.data || {};
        hydrated.push({
          ...supplier,
          ...detailData,
          supplier_data: detailData?.supplier_data || supplier.supplier_data
        });
      } catch (error) {
        hydrated.push(supplier);
      }
    }
    return hydrated;
  };

  const loadSupplierDetails = async (supplierId) => {
    const response = await axios.get(
      API_ENDPOINTS.SUPPLIERS_DETAIL(supplierId),
      { headers: getAuthHeaders() }
    );
    return response.data;
  };

  const fetchSuppliers = async () => {
    setIsLoadingSuppliers(true);
    try {
      const headers = getAuthHeaders();
      let url = API_ENDPOINTS.SUPPLIERS_LIST;
      let aggregatedSuppliers = [];
      let safetyCounter = 0;
      const MAX_PAGES = 20;

      while (url && safetyCounter < MAX_PAGES) {
        const response = await axios.get(url, { headers });
        const pageSuppliers = extractSuppliersFromPayload(response.data);
        aggregatedSuppliers = aggregatedSuppliers.concat(pageSuppliers);
        const nextUrl = response.data?.next || response.data?.links?.next || response.data?.pagination?.next;
        url = resolveUrl(nextUrl, API_ENDPOINTS.SUPPLIERS_LIST);
        if (!url) {
          break;
        }
        safetyCounter += 1;
      }

      if (needsSupplierDetailHydration(aggregatedSuppliers)) {
        setIsHydratingSuppliers(true);
        aggregatedSuppliers = await hydrateSuppliersWithDetails(aggregatedSuppliers, headers);
        setIsHydratingSuppliers(false);
      }

      setSuppliers(aggregatedSuppliers);
    } catch (error) {
      setSuppliers([]);
      setIsHydratingSuppliers(false);
      const apiError = error?.response?.data?.message || error?.response?.data?.detail || 'Unable to load suppliers.';
      setNotification({ type: 'error', message: apiError });
    } finally {
      setIsLoadingSuppliers(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'suppliers') {
      fetchSuppliers();
    }
  }, [activeTab]);

  // Fetch clients when clients tab is active
  const fetchClients = async () => {
    setIsLoadingClients(true);
    try {
      const token = localStorage.getItem('token');
      const headers = {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      const response = await axios.get(API_ENDPOINTS.CLIENTS_LIST, { headers });
      const clientsData = Array.isArray(response.data)
        ? response.data
        : (response.data?.results || response.data?.data || response.data?.clients || []);

      setClients(clientsData);
    } catch (error) {
      setClients([]);
      const apiError = error?.response?.data?.message || error?.response?.data?.detail || 'Unable to load clients.';
      setNotification({ type: 'error', message: apiError });
    } finally {
      setIsLoadingClients(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'clients') {
      fetchClients();
    }
  }, [activeTab]);

  const handleAddClient = () => {
    setSelectedClient(null);
    setClientModalMode('add');
    setIsClientModalOpen(true);
  };

  const handleEditClient = (client) => {
    if (!client?.id) return;
    setSelectedClient(client);
    setClientModalMode('edit');
    setIsClientModalOpen(true);
  };

  const handleClientSave = () => {
    fetchClients();
    setNotification({
      type: 'success',
      message: clientModalMode === 'edit' ? 'Client updated successfully.' : 'Client created successfully.'
    });
  };

  const handleCloseClientModal = () => {
    setIsClientModalOpen(false);
    setSelectedClient(null);
  };

  const handleDeleteClient = async (client) => {
    if (!client?.id) return;

    const confirmed = window.confirm(`Are you sure you want to delete "${client.name || 'this client'}"?`);
    if (!confirmed) return;

    setDeletingClientId(client.id);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(API_ENDPOINTS.CLIENTS_DELETE(client.id), {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      setNotification({
        type: 'success',
        message: 'Client deleted successfully.'
      });
      await fetchClients();
    } catch (error) {
      const apiError =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        'Failed to delete client.';
      setNotification({
        type: 'error',
        message: apiError
      });
    } finally {
      setDeletingClientId(null);
    }
  };

  const handleConsolidatedInvoices = async (entity) => {
    if (!entity) return;
    setConsolidatedCustomer(entity);
    setIsConsolidatedOpen(true);
    setIsConsolidatedLoading(true);
    setConsolidatedError('');
    setConsolidatedInvoices([]);
    try {
      const headers = getAuthHeaders(true);
      const basicInfo = entity.customer_data?.basic_information || {};
      const searchName =
        entity.name ||
        entity.client_name ||
        entity.company_name ||
        basicInfo.customer_name ||
        basicInfo.display_name ||
        '';
      const params = new URLSearchParams();
      if (searchName) params.append('search', searchName);
      params.append('ordering', '-invoice_date');

      const response = await axios.get(
        `${API_ENDPOINTS.INVOICES_LIST}?${params.toString()}`,
        { headers }
      );

      const invoicesData = Array.isArray(response.data) ? response.data : (response.data?.results || []);

      // Fetch payment status for each invoice in parallel
      const withPaymentStatus = await Promise.all(
        invoicesData.map(async (inv) => {
          try {
            const statusRes = await axios.get(API_ENDPOINTS.INVOICES_PAYMENT_STATUS(inv.id), { headers });
            return { ...inv, payment_status: statusRes.data };
          } catch {
            return { ...inv, payment_status: null };
          }
        })
      );

      setConsolidatedInvoices(withPaymentStatus);
    } catch (error) {
      const apiError =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        'Failed to load consolidated invoices.';
      setConsolidatedError(apiError);
    } finally {
      setIsConsolidatedLoading(false);
    }
  };

  const handleAddSupplier = () => {
    setSelectedSupplier(null);
    setSupplierModalMode('add');
    setIsSupplierModalOpen(true);
  };

  const handleEditSupplier = async (supplier) => {
    if (!supplier?.id) return;
    setPrefetchingSupplierId(supplier.id);
    try {
      const detailedSupplier = await loadSupplierDetails(supplier.id);
      setSelectedSupplier(detailedSupplier);
      setSupplierModalMode('edit');
      setIsSupplierModalOpen(true);
    } catch (error) {
      const apiError = error?.response?.data?.message || 'Unable to load supplier details for editing.';
      setNotification({ type: 'error', message: apiError });
    } finally {
      setPrefetchingSupplierId(null);
    }
  };

  const handleSupplierSave = () => {
    fetchSuppliers();
    setNotification({
      type: 'success',
      message: supplierModalMode === 'edit' ? 'Supplier updated successfully.' : 'Supplier created successfully.'
    });
  };

  const handleCloseSupplierModal = () => {
    setIsSupplierModalOpen(false);
    setSelectedSupplier(null);
  };

  const handleViewSupplier = async (supplierId) => {
    if (!supplierId) return;
    setViewingSupplierId(supplierId);
    setIsSupplierDetailsModalOpen(true);
    setIsSupplierDetailsLoading(true);
    setSupplierDetailsError('');
    setSupplierDetails(null);
    try {
      const detail = await loadSupplierDetails(supplierId);
      setSupplierDetails(detail);
    } catch (error) {
      const apiError = error?.response?.data?.message || error?.response?.data?.detail || 'Unable to fetch supplier details.';
      setSupplierDetailsError(apiError);
    } finally {
      setIsSupplierDetailsLoading(false);
      setViewingSupplierId(null);
    }
  };

  const handleCloseSupplierDetails = () => {
    setIsSupplierDetailsModalOpen(false);
    setSupplierDetails(null);
    setSupplierDetailsError('');
  };

  const handleDeleteSupplierRequest = (supplier) => {
    if (!supplier?.id) return;
    setSupplierPendingDelete(supplier);
  };

  const handleDeleteSupplierConfirm = async () => {
    const supplierId = supplierPendingDelete?.id;
    if (!supplierId) return;

    setDeletingSupplierId(supplierId);
    try {
      await axios.delete(API_ENDPOINTS.SUPPLIERS_DELETE(supplierId), {
        headers: getAuthHeaders()
      });
      const apiMessage = 'Supplier deleted successfully.';
      setNotification({ type: 'success', message: apiMessage });
      await fetchSuppliers();
    } catch (error) {
      const apiError = error?.response?.data?.message || error?.response?.data?.detail || 'Failed to delete supplier.';
      setNotification({ type: 'error', message: apiError });
    } finally {
      setDeletingSupplierId(null);
      setSupplierPendingDelete(null);
    }
  };

  const renderCustomersTab = () => {
    const activeCustomers = customers.filter(c => c.is_active !== false).length;
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filteredCustomers = customers.filter((customer) => {
      if (!normalizedSearch) return true;
      const customerData = customer?.customer_data || {};
      const basicInfo = customerData?.basic_information || customerData;
      const billingAddress = customerData?.billing_address || {};
      const searchableFields = [
        basicInfo?.customer_name,
        basicInfo?.display_name,
        basicInfo?.business_name,
        basicInfo?.email_address,
        basicInfo?.mobile_number,
        basicInfo?.customer_gstin,
        billingAddress?.city,
        billingAddress?.state
      ];
      return searchableFields.some((field) => {
        if (!field) return false;
        return String(field).toLowerCase().includes(normalizedSearch);
      });
    });
    const showNoCustomers = !isLoading && customers.length === 0;
    const showNoMatches = !isLoading && customers.length > 0 && filteredCustomers.length === 0;

    const notificationStyles = notification?.type === 'error'
      ? 'border-red-200 bg-red-50 text-red-800'
      : notification?.type === 'success'
        ? 'border-green-200 bg-green-50 text-green-800'
        : 'border-blue-200 bg-blue-50 text-blue-800';

    const renderNotificationIcon = () => {
      if (notification?.type === 'error') {
        return <AlertCircle className="w-5 h-5 mt-0.5" />;
      }
      if (notification?.type === 'success') {
        return <CheckCircle className="w-5 h-5 mt-0.5" />;
      }
      return <Info className="w-5 h-5 mt-0.5" />;
    };
    
    return (
      <div className="space-y-6">
        {notification?.message && (
          <div className={`flex items-start gap-3 p-4 rounded-lg border ${notificationStyles}`}>
            {renderNotificationIcon()}
            <p className="text-sm">{notification.message}</p>
          </div>
        )}

        {/* Customer Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Customers</p>
                <p className="text-2xl font-bold text-foreground">{customers.length}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-sm text-blue-600 mt-2">
              {activeCustomers} Active
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Customers</p>
                <p className="text-2xl font-bold text-foreground">{activeCustomers}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <p className="text-sm text-green-600 mt-2">Currently active</p>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">New This Month</p>
                <p className="text-2xl font-bold text-foreground">
                  {customers.filter(c => {
                    if (!c?.created_at) return false;
                    const createdDate = new Date(c.created_at);
                    if (Number.isNaN(createdDate.getTime())) return false;
                    const now = new Date();
                    return createdDate.getMonth() === now.getMonth() && 
                           createdDate.getFullYear() === now.getFullYear();
                  }).length}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <p className="text-sm text-purple-600 mt-2">This month</p>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Inactive</p>
                <p className="text-2xl font-bold text-foreground">
                  {Math.max(customers.length - activeCustomers, 0)}
                </p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <TrendingUp className="w-6 h-6 text-orange-600" />
              </div>
            </div>
            <p className="text-sm text-orange-600 mt-2">Needs attention</p>
          </div>
        </div>

        {/* Customer Actions */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Customer Management</h3>
            <Button onClick={handleAddCustomer}>
              <Plus className="w-4 h-4 mr-2" />
              Add Customer
            </Button>
          </div>
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search customers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <Button variant="outline">
            <Filter className="w-4 h-4 mr-2" />
            Filter
          </Button>
        </div>
      </div>

        {/* Customer List */}
        {isHydratingCustomers && (
          <div className="flex items-center gap-3 p-3 mb-4 text-sm text-blue-700 bg-blue-50 border border-blue-100 rounded-lg">
            <span className="inline-block w-4 h-4 border-2 border-blue-200 border-t-blue-700 rounded-full animate-spin" />
            <span>Loading full customer details…</span>
          </div>
        )}
        <div className="bg-white rounded-lg border border-gray-200">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                <p className="mt-4 text-sm text-muted-foreground">Loading customers...</p>
              </div>
            </div>
          ) : showNoCustomers ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Users className="w-12 h-12 text-gray-400 mb-4" />
              <p className="text-lg font-medium text-gray-900 mb-2">No customers yet</p>
              <p className="text-sm text-muted-foreground mb-4">Get started by adding your first customer</p>
              <Button onClick={handleAddCustomer}>
                <Plus className="w-4 h-4 mr-2" />
                Add Customer
              </Button>
            </div>
          ) : showNoMatches ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <Search className="w-10 h-10 text-gray-400 mb-3" />
              <p className="text-lg font-medium text-gray-900 mb-1">No matching customers</p>
              <p className="text-sm text-muted-foreground mb-4">
                Try adjusting your search term to find what you need.
              </p>
              <Button variant="outline" onClick={() => setSearchTerm('')}>
                Clear search
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      GSTIN
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      City
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Actions
                    </th>
                      {/* <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Actions
                      </th> */}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredCustomers.map((customer) => {
                    const customerData = customer?.customer_data || {};
                    const basicInfo = customerData?.basic_information || customerData;
                    const billingAddress = customerData?.billing_address || customerData?.address || {};
                    const avatarSource = basicInfo?.customer_name || basicInfo?.display_name || basicInfo?.business_name || '??';
                    const avatar = avatarSource?.substring ? avatarSource.substring(0, 2).toUpperCase() : '??';
                    
                    return (
                      <tr key={customer.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-primary rounded-full flex items-center justify-center">
                              <span className="text-white font-medium text-sm">{avatar}</span>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-foreground">
                                {basicInfo?.customer_name || basicInfo?.display_name || 'N/A'}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {basicInfo?.business_name || ''}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col space-y-1">
                            <div className="flex items-center text-sm text-muted-foreground">
                              <Mail className="w-4 h-4 mr-2" />
                              {basicInfo?.email_address || customerData?.email_address || 'N/A'}
                            </div>
                            <div className="flex items-center text-sm text-muted-foreground">
                              <Phone className="w-4 h-4 mr-2" />
                              {basicInfo?.mobile_number || basicInfo?.phone_number || customerData?.phone_number || 'N/A'}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-foreground">
                            {basicInfo?.customer_gstin || customerData?.customer_gstin || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-foreground">
                            {billingAddress?.city || 'N/A'}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {billingAddress?.state || ''}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            customer.is_active !== false
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {customer.is_active !== false ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                          {formatDisplayDate(customer?.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-3">
                            <button
                              type="button"
                              title="View details"
                              aria-label="View customer details"
                              className="inline-flex items-center gap-1 rounded-full border border-blue-100 px-3 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => handleViewCustomer(customer.id)}
                              disabled={viewingCustomerId === customer.id}
                            >
                              {viewingCustomerId === customer.id ? (
                                <span className="inline-block w-4 h-4 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                              ) : (
                                <>
                                  <Eye className="w-3.5 h-3.5" />
                                  <span>View</span>
                                </>
                              )}
                            </button>
                            <button 
                              type="button"
                              title="Edit customer"
                              aria-label="Edit customer"
                              className="inline-flex items-center gap-1 rounded-full border border-green-100 px-3 py-1 text-xs font-semibold text-green-700 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => handleEditCustomer(customer)}
                              disabled={prefetchingCustomerId === customer.id}
                            >
                              {prefetchingCustomerId === customer.id ? (
                                <span className="inline-block w-4 h-4 border-2 border-green-200 border-t-green-600 rounded-full animate-spin" />
                              ) : (
                                <>
                                  <Edit className="w-3.5 h-3.5" />
                                  <span>Edit</span>
                                </>
                              )}
                            </button>
                            <button
                              type="button"
                              title="Delete customer"
                              aria-label="Delete customer"
                              className="inline-flex items-center gap-1 rounded-full border border-red-100 px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => handleDeleteCustomerRequest(customer)}
                              disabled={deletingCustomerId === customer.id}
                            >
                              {deletingCustomerId === customer.id ? (
                                <span className="inline-block w-4 h-4 border-2 border-red-200 border-t-red-600 rounded-full animate-spin" />
                              ) : (
                                <>
                                  <Trash2 className="w-3.5 h-3.5" />
                                  <span>Delete</span>
                                </>
                              )}
                            </button>
                            <button
                              type="button"
                              title="Consolidated invoices"
                              aria-label="Consolidated invoices"
                              className="inline-flex items-center gap-1 rounded-full border border-blue-100 px-3 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => handleConsolidatedInvoices(customer)}
                            >
                              <FileText className="w-3.5 h-3.5" />
                              <span>Consolidated Invoices</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderClientsTab = () => {
    const totalClients = clients.length;
    const activeClients = clients.filter(
      (c) => c?.status === 'Active' || c?.is_active !== false
    ).length;
    const inactiveClients = Math.max(totalClients - activeClients, 0);

    const newThisMonth = clients.filter((c) => {
      const created = c?.created_at || c?.createdAt;
      if (!created) return false;
      const createdDate = new Date(created);
      if (Number.isNaN(createdDate.getTime())) return false;
      const now = new Date();
      return (
        createdDate.getMonth() === now.getMonth() &&
        createdDate.getFullYear() === now.getFullYear()
      );
    }).length;

    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filteredClients = clients.filter((client) => {
      if (!normalizedSearch) return true;
      const searchableFields = [
        client?.name,
        client?.email,
        client?.contact_person,
        client?.branch
      ];
      return searchableFields.some((field) => {
        if (!field) return false;
        return String(field).toLowerCase().includes(normalizedSearch);
      });
    });

    const showNoClients = !isLoadingClients && clients.length === 0;
    const showNoMatches = !isLoadingClients && clients.length > 0 && filteredClients.length === 0;

    const notificationStyles = notification?.type === 'error'
      ? 'border-red-200 bg-red-50 text-red-800'
      : notification?.type === 'success'
        ? 'border-green-200 bg-green-50 text-green-800'
        : 'border-blue-200 bg-blue-50 text-blue-800';

    const renderNotificationIcon = () => {
      if (notification?.type === 'error') {
        return <AlertCircle className="w-5 h-5 mt-0.5" />;
      }
      if (notification?.type === 'success') {
        return <CheckCircle className="w-5 h-5 mt-0.5" />;
      }
      return <Info className="w-5 h-5 mt-0.5" />;
    };

    return (
      <div className="space-y-6">
        {notification?.message && (
          <div className={`flex items-start gap-3 p-4 rounded-lg border ${notificationStyles}`}>
            {renderNotificationIcon()}
            <p className="text-sm">{notification.message}</p>
          </div>
        )}

        {/* Client Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Clients</p>
                <p className="text-2xl font-bold text-foreground">{totalClients}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-sm text-blue-600 mt-2">
              {activeClients} Active
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Clients</p>
                <p className="text-2xl font-bold text-foreground">{activeClients}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <p className="text-sm text-green-600 mt-2">Currently active</p>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">New This Month</p>
                <p className="text-2xl font-bold text-foreground">{newThisMonth}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <p className="text-sm text-purple-600 mt-2">This month</p>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Inactive</p>
                <p className="text-2xl font-bold text-foreground">{inactiveClients}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <TrendingUp className="w-6 h-6 text-orange-600" />
              </div>
            </div>
            <p className="text-sm text-orange-600 mt-2">Needs attention</p>
          </div>
        </div>

        {/* Client Actions */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Client Directory</h3>
            <Button onClick={handleAddClient}>
              <Plus className="w-4 h-4 mr-2" />
              Add Client
            </Button>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search clients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <Button variant="outline">
              <Filter className="w-4 h-4 mr-2" />
              Filter
            </Button>
          </div>
        </div>

        {/* Client List */}
        <div className="bg-white rounded-lg border border-gray-200">
          {isLoadingClients ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                <p className="mt-4 text-sm text-muted-foreground">Loading clients...</p>
              </div>
            </div>
          ) : showNoClients ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Users className="w-12 h-12 text-gray-400 mb-4" />
              <p className="text-lg font-medium text-gray-900 mb-2">No clients yet</p>
              <p className="text-sm text-muted-foreground mb-4">
                Clients will appear here once added in the Client Management module.
              </p>
            </div>
          ) : showNoMatches ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <Search className="w-10 h-10 text-gray-400 mb-3" />
              <p className="text-lg font-medium text-gray-900 mb-1">No matching clients</p>
              <p className="text-sm text-muted-foreground mb-4">
                Try adjusting your search term to find what you need.
              </p>
              <Button variant="outline" onClick={() => setSearchTerm('')}>
                Clear search
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Branch
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredClients.map((client) => {
                    const avatarSource = client?.name || client?.contact_person || '??';
                    const avatar = avatarSource?.substring
                      ? avatarSource.substring(0, 2).toUpperCase()
                      : '??';

                    return (
                      <tr key={client.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-primary rounded-full flex items-center justify-center">
                              <span className="text-white font-medium text-sm">{avatar}</span>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-foreground">
                                {client?.name || 'N/A'}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {client?.branch || ''}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col space-y-1">
                            <div className="flex items-center text-sm text-muted-foreground">
                              <Mail className="w-4 h-4 mr-2" />
                              {client?.email || 'N/A'}
                            </div>
                            <div className="flex items-center text-sm text-muted-foreground">
                              <Phone className="w-4 h-4 mr-2" />
                              {client?.contact_person || 'N/A'}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-foreground">
                            {client?.branch || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              client?.status === 'Active' || client?.is_active !== false
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {client?.status || (client?.is_active === false ? 'Inactive' : 'Active')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                          {formatDisplayDate(client?.created_at || client?.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-3">
                            <button
                              type="button"
                              title="Edit client"
                              aria-label="Edit client"
                              className="inline-flex items-center gap-1 rounded-full border border-green-100 px-3 py-1 text-xs font-semibold text-green-700 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => handleEditClient(client)}
                            >
                              <Edit className="w-3.5 h-3.5" />
                              <span>Edit</span>
                            </button>
                            <button
                              type="button"
                              title="Delete client"
                              aria-label="Delete client"
                              className="inline-flex items-center gap-1 rounded-full border border-red-100 px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => handleDeleteClient(client)}
                              disabled={deletingClientId === client.id}
                            >
                              {deletingClientId === client.id ? (
                                <span className="inline-block w-4 h-4 border-2 border-red-200 border-t-red-600 rounded-full animate-spin" />
                              ) : (
                                <>
                                  <Trash2 className="w-3.5 h-3.5" />
                                  <span>Delete</span>
                                </>
                              )}
                            </button>
                            <button
                              type="button"
                              title="Consolidated invoices"
                              aria-label="Consolidated invoices"
                              className="inline-flex items-center gap-1 rounded-full border border-blue-100 px-3 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => handleConsolidatedInvoices(client)}
                            >
                              <FileText className="w-3.5 h-3.5" />
                              <span>Consolidated Invoices</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSuppliersTab = () => {
    const activeSuppliers = suppliers.filter(s => s.is_active !== false).length;
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filteredSuppliers = suppliers.filter((supplier) => {
      if (!normalizedSearch) return true;
      const supplierData = supplier?.supplier_data || {};
      const basicInfo = supplierData?.basic_information || supplierData;
      const billingAddress = supplierData?.billing_address || {};
      const searchableFields = [
        basicInfo?.supplier_name,
        basicInfo?.display_name,
        basicInfo?.business_name,
        basicInfo?.email_address,
        basicInfo?.mobile_number,
        basicInfo?.supplier_gstin,
        billingAddress?.city,
        billingAddress?.state
      ];
      return searchableFields.some((field) => {
        if (!field) return false;
        return String(field).toLowerCase().includes(normalizedSearch);
      });
    });
    const showNoSuppliers = !isLoadingSuppliers && suppliers.length === 0;
    const showNoMatches = !isLoadingSuppliers && suppliers.length > 0 && filteredSuppliers.length === 0;

    const notificationStyles = notification?.type === 'error'
      ? 'border-red-200 bg-red-50 text-red-800'
      : notification?.type === 'success'
        ? 'border-green-200 bg-green-50 text-green-800'
        : 'border-blue-200 bg-blue-50 text-blue-800';

    const renderNotificationIcon = () => {
      if (notification?.type === 'error') {
        return <AlertCircle className="w-5 h-5 mt-0.5" />;
      }
      if (notification?.type === 'success') {
        return <CheckCircle className="w-5 h-5 mt-0.5" />;
      }
      return <Info className="w-5 h-5 mt-0.5" />;
    };

    return (
      <div className="space-y-6">
        {notification?.message && (
          <div className={`flex items-start gap-3 p-4 rounded-lg border ${notificationStyles}`}>
            {renderNotificationIcon()}
            <p className="text-sm">{notification.message}</p>
          </div>
        )}

        {isHydratingSuppliers && (
          <div className="flex items-center gap-2 p-3 rounded-lg border border-blue-200 bg-blue-50 text-blue-800">
            <Info className="w-4 h-4" />
            <p className="text-sm">Loading full supplier details...</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Suppliers</p>
                <p className="text-2xl font-bold text-foreground">{suppliers.length}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-sm text-blue-600 mt-2">
              {activeSuppliers} Active
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Suppliers</p>
                <p className="text-2xl font-bold text-foreground">{activeSuppliers}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <p className="text-sm text-green-600 mt-2">Currently active</p>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">New This Month</p>
                <p className="text-2xl font-bold text-foreground">
                  {suppliers.filter(s => {
                    if (!s?.created_at) return false;
                    const createdDate = new Date(s.created_at);
                    if (Number.isNaN(createdDate.getTime())) return false;
                    const now = new Date();
                    return createdDate.getMonth() === now.getMonth() && 
                           createdDate.getFullYear() === now.getFullYear();
                  }).length}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <p className="text-sm text-purple-600 mt-2">This month</p>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Inactive</p>
                <p className="text-2xl font-bold text-foreground">
                  {Math.max(suppliers.length - activeSuppliers, 0)}
                </p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <TrendingUp className="w-6 h-6 text-orange-600" />
              </div>
            </div>
            <p className="text-sm text-orange-600 mt-2">Needs attention</p>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Supplier Management</h3>
            <Button onClick={handleAddSupplier}>
              <Plus className="w-4 h-4 mr-2" />
              Add Supplier
            </Button>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search suppliers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <Button variant="outline">
              <Filter className="w-4 h-4 mr-2" />
              Filter
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200">
          {isLoadingSuppliers ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                <p className="mt-4 text-sm text-muted-foreground">Loading suppliers...</p>
              </div>
            </div>
          ) : showNoSuppliers ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Package className="w-12 h-12 text-gray-400 mb-4" />
              <p className="text-lg font-medium text-gray-900 mb-2">No suppliers yet</p>
              <p className="text-sm text-muted-foreground mb-4">Get started by adding your first supplier</p>
              <Button onClick={handleAddSupplier}>
                <Plus className="w-4 h-4 mr-2" />
                Add Supplier
              </Button>
            </div>
          ) : showNoMatches ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <Search className="w-10 h-10 text-gray-400 mb-3" />
              <p className="text-lg font-medium text-gray-900 mb-1">No matching suppliers</p>
              <p className="text-sm text-muted-foreground mb-4">
                Try adjusting your search term to find what you need.
              </p>
              <Button variant="outline" onClick={() => setSearchTerm('')}>
                Clear search
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Supplier
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      GSTIN
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      City
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredSuppliers.map((supplier) => {
                    const supplierData = supplier?.supplier_data || {};
                    const basicInfo = supplierData?.basic_information || supplierData;
                    const billingAddress = supplierData?.billing_address || {};
                    const avatarSource = basicInfo?.supplier_name || basicInfo?.display_name || basicInfo?.business_name || '??';
                    const avatar = avatarSource?.substring ? avatarSource.substring(0, 2).toUpperCase() : '??';
                    
                    return (
                      <tr key={supplier.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-purple-600 rounded-full flex items-center justify-center">
                              <span className="text-white font-medium text-sm">{avatar}</span>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-foreground">
                                {basicInfo?.supplier_name || basicInfo?.display_name || 'N/A'}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {basicInfo?.business_name || ''}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col space-y-1">
                            <div className="flex items-center text-sm text-muted-foreground">
                              <Mail className="w-4 h-4 mr-2" />
                              {basicInfo?.email_address || supplierData?.email_address || 'N/A'}
                            </div>
                            <div className="flex items-center text-sm text-muted-foreground">
                              <Phone className="w-4 h-4 mr-2" />
                              {basicInfo?.mobile_number || basicInfo?.phone_number || supplierData?.phone_number || 'N/A'}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-foreground">
                            {basicInfo?.supplier_gstin || supplierData?.supplier_gstin || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-foreground">
                            {billingAddress?.city || 'N/A'}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {billingAddress?.state || ''}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            supplier.is_active !== false
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {supplier.is_active !== false ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                          {formatDisplayDate(supplier?.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-3">
                            <button
                              type="button"
                              title="View details"
                              aria-label="View supplier details"
                              className="inline-flex items-center gap-1 rounded-full border border-blue-100 px-3 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => handleViewSupplier(supplier.id)}
                              disabled={viewingSupplierId === supplier.id}
                            >
                              {viewingSupplierId === supplier.id ? (
                                <span className="inline-block w-4 h-4 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                              ) : (
                                <>
                                  <Eye className="w-3.5 h-3.5" />
                                  <span>View</span>
                                </>
                              )}
                            </button>
                            <button 
                              type="button"
                              title="Edit supplier"
                              aria-label="Edit supplier"
                              className="inline-flex items-center gap-1 rounded-full border border-green-100 px-3 py-1 text-xs font-semibold text-green-700 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => handleEditSupplier(supplier)}
                              disabled={prefetchingSupplierId === supplier.id}
                            >
                              {prefetchingSupplierId === supplier.id ? (
                                <span className="inline-block w-4 h-4 border-2 border-green-200 border-t-green-600 rounded-full animate-spin" />
                              ) : (
                                <>
                                  <Edit className="w-3.5 h-3.5" />
                                  <span>Edit</span>
                                </>
                              )}
                            </button>
                            <button
                              type="button"
                              title="Delete supplier"
                              aria-label="Delete supplier"
                              className="inline-flex items-center gap-1 rounded-full border border-red-100 px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => handleDeleteSupplierRequest(supplier)}
                              disabled={deletingSupplierId === supplier.id}
                            >
                              {deletingSupplierId === supplier.id ? (
                                <span className="inline-block w-4 h-4 border-2 border-red-200 border-t-red-600 rounded-full animate-spin" />
                              ) : (
                                <>
                                  <Trash2 className="w-3.5 h-3.5" />
                                  <span>Delete</span>
                                </>
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-2">
          <div className="p-2 bg-primary rounded-lg">
            <Users className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-foreground">Contacts</h1>
        </div>
        <p className="text-muted-foreground">
          Manage your customers and suppliers information
        </p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px space-x-8 px-6" aria-label="Tabs">
            {[
              { id: 'customers', label: 'Customers', icon: Building2 },
              { id: 'suppliers', label: 'Suppliers', icon: Package },
              { id: 'clients', label: 'Clients', icon: Users }
            ].map((tab) => {
              const IconComponent = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                  }`}
                >
                  <IconComponent className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'customers' && renderCustomersTab()}
          {activeTab === 'suppliers' && renderSuppliersTab()}
          {activeTab === 'clients' && renderClientsTab()}
        </div>
      </div>

      {/* Customer Modal */}
      <CustomerModal
        isOpen={isCustomerModalOpen}
        onClose={handleCloseModal}
        customer={selectedCustomer}
        onSave={handleCustomerSave}
        mode={modalMode}
      />
      <CustomerDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={handleCloseDetails}
        customer={customerDetails}
        isLoading={isDetailsLoading}
        error={detailsError}
      />
      {customerPendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg border border-gray-200 w-full max-w-md shadow-xl">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-50 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm uppercase tracking-wide text-red-600 font-semibold">Delete Customer</p>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {customerPendingDelete?.customer_data?.basic_information?.customer_name || 'Confirm deletion'}
                  </h3>
                </div>
              </div>
              <p className="mt-4 text-sm text-muted-foreground">
                This action cannot be undone. The customer record and its related data will be permanently removed.
              </p>
            </div>
            <div className="p-6 flex flex-col gap-4">
              <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                <p className="text-xs font-semibold text-gray-500 tracking-wide uppercase mb-1">Customer details</p>
                <p className="text-sm text-gray-900">
                  {customerPendingDelete?.customer_data?.basic_information?.customer_name ||
                    customerPendingDelete?.customer_name ||
                    'N/A'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {customerPendingDelete?.customer_data?.basic_information?.email_address ||
                    customerPendingDelete?.customer_data?.basic_information?.mobile_number ||
                    '—'}
                </p>
              </div>
              <div className="flex items-center justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  disabled={deletingCustomerId === customerPendingDelete?.id}
                  onClick={() => setCustomerPendingDelete(null)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  disabled={deletingCustomerId === customerPendingDelete?.id}
                  onClick={handleDeleteCustomerConfirm}
                >
                  {deletingCustomerId === customerPendingDelete?.id ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Consolidated Invoices Modal */}
      {isConsolidatedOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg border border-gray-200 w-full max-w-5xl max-h-[90vh] overflow-hidden shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <p className="text-sm text-muted-foreground">Consolidated invoices</p>
                <h3 className="text-xl font-semibold text-gray-900">
                  {consolidatedCustomer?.name ||
                    consolidatedCustomer?.client_name ||
                    consolidatedCustomer?.customer_data?.basic_information?.customer_name ||
                    'Customer'}
                </h3>
              </div>
              <Button variant="outline" onClick={() => { setIsConsolidatedOpen(false); setConsolidatedInvoices([]); }}>
                Close
              </Button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto max-h-[75vh]">
              {consolidatedError && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  <span>{consolidatedError}</span>
                </div>
              )}

              {/* Summary cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="rounded-md border border-gray-200 bg-white p-4 shadow-sm">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Total Invoiced</p>
                  <p className="text-2xl font-bold text-foreground mt-1">{formatCurrency(consolidatedTotals.totalInvoiced)}</p>
                </div>
                <div className="rounded-md border border-green-200 bg-green-50 p-4 shadow-sm">
                  <p className="text-xs uppercase tracking-wide text-green-700">Total Paid</p>
                  <p className="text-2xl font-bold text-green-800 mt-1">{formatCurrency(consolidatedTotals.totalPaid)}</p>
                </div>
                <div className="rounded-md border border-yellow-200 bg-yellow-50 p-4 shadow-sm">
                  <p className="text-xs uppercase tracking-wide text-yellow-700">Partially Paid</p>
                  <p className="text-2xl font-bold text-yellow-800 mt-1">{formatCurrency(consolidatedTotals.totalPartial)}</p>
                </div>
                <div className="rounded-md border border-red-200 bg-red-50 p-4 shadow-sm">
                  <p className="text-xs uppercase tracking-wide text-red-700">Pending Amount</p>
                  <p className="text-2xl font-bold text-red-800 mt-1">{formatCurrency(consolidatedTotals.totalPending)}</p>
                </div>
              </div>

              {isConsolidatedLoading ? (
                <div className="py-12 flex flex-col items-center justify-center text-muted-foreground">
                  <div className="w-10 h-10 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-3" />
                  <p>Loading consolidated invoices...</p>
                </div>
              ) : consolidatedInvoices.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p>No invoices found for this customer.</p>
                </div>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="min-w-full bg-white">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 border-b">Invoice #</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 border-b">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 border-b">Due Date</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 border-b">Amount</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 border-b">Paid</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 border-b">Pending</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 border-b">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 border-b">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {consolidatedInvoices.map((inv) => (
                        <tr key={inv.id} className="hover:bg-gray-50">
                          <td className="px-4 py-2 border-b">
                            <div className="text-sm font-semibold text-foreground">{inv.invoice_number || 'N/A'}</div>
                            <div className="text-xs text-muted-foreground capitalize">{inv.type || 'N/A'}</div>
                          </td>
                          <td className="px-4 py-2 border-b text-sm text-foreground">
                            {inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="px-4 py-2 border-b text-sm text-foreground">
                            {inv.due_date ? new Date(inv.due_date).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="px-4 py-2 border-b text-sm font-semibold text-foreground">
                            {formatCurrency(inv.total_amount)}
                          </td>
                          <td className="px-4 py-2 border-b text-sm">
                            {formatCurrency(inv.payment_status?.fully_paid)}
                          </td>
                          <td className="px-4 py-2 border-b text-sm font-medium text-foreground">
                            {formatCurrency(inv.payment_status?.receivable)}
                          </td>
                          <td className="px-4 py-2 border-b text-sm">
                            <span
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                inv.status === 'paid'
                                  ? 'bg-green-100 text-green-800'
                                  : inv.status === 'sent'
                                    ? 'bg-blue-100 text-blue-800'
                                    : inv.status === 'overdue'
                                      ? 'bg-red-100 text-red-800'
                                      : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {inv.status || 'N/A'}
                            </span>
                          </td>
                          <td className="px-4 py-2 border-b text-sm">
                            <div className="flex gap-2">
                              <Button variant="outline" size="sm">
                                View
                              </Button>
                              <Button variant="outline" size="sm">
                                Pay
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Payment history placeholder (no data available in current API response) */}
              <div className="mt-6 border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b bg-gray-50">
                  <h4 className="text-sm font-semibold text-foreground">Payment History</h4>
                </div>
                <div className="p-4 text-sm text-muted-foreground text-center">
                  No payment history found.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Supplier Modals */}
      <SupplierModal
        isOpen={isSupplierModalOpen}
        onClose={handleCloseSupplierModal}
        supplier={selectedSupplier}
        onSave={handleSupplierSave}
        mode={supplierModalMode}
      />
      <SupplierDetailsModal
        isOpen={isSupplierDetailsModalOpen}
        onClose={handleCloseSupplierDetails}
        supplier={supplierDetails}
        isLoading={isSupplierDetailsLoading}
        error={supplierDetailsError}
      />
      {/* Client Modal */}
      <ClientModal
        isOpen={isClientModalOpen}
        onClose={handleCloseClientModal}
        client={selectedClient}
        onSave={handleClientSave}
        mode={clientModalMode}
      />
      {supplierPendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg border border-gray-200 w-full max-w-md shadow-xl">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-50 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm uppercase tracking-wide text-red-600 font-semibold">Delete Supplier</p>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {supplierPendingDelete?.supplier_data?.basic_information?.supplier_name || 'Confirm deletion'}
                  </h3>
                </div>
              </div>
              <p className="mt-4 text-sm text-muted-foreground">
                This action cannot be undone. The supplier record and its related data will be permanently removed.
              </p>
            </div>
            <div className="p-6 flex flex-col gap-4">
              <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                <p className="text-xs font-semibold text-gray-500 tracking-wide uppercase mb-1">Supplier details</p>
                <p className="text-sm text-gray-900">
                  {supplierPendingDelete?.supplier_data?.basic_information?.supplier_name ||
                    supplierPendingDelete?.supplier_name ||
                    'N/A'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {supplierPendingDelete?.supplier_data?.basic_information?.email_address ||
                    supplierPendingDelete?.supplier_data?.basic_information?.mobile_number ||
                    '—'}
                </p>
              </div>
              <div className="flex items-center justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  disabled={deletingSupplierId === supplierPendingDelete?.id}
                  onClick={() => setSupplierPendingDelete(null)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  disabled={deletingSupplierId === supplierPendingDelete?.id}
                  onClick={handleDeleteSupplierConfirm}
                >
                  {deletingSupplierId === supplierPendingDelete?.id ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contacts;

